import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { DictionaryItem } from '@hglobal/common';
import {
  GroupTagEnum,
  LimitApprovalStatus,
  MessageStatus,
  SortType,
  TgChatPermissions,
  TgChatTypes,
  TgUserPermissions,
  TransactionDirection,
  UserRoles,
  UserStatusType,
} from '@pt/models';

@Injectable({
  providedIn: 'root',
})
export class DictionariesService {
  languages: DictionaryItem<string>[] = [
    {
      id: 'EN',
      name: this._translate.instant('dictionary.languages.en'),
    },
    {
      id: 'RU',
      name: this._translate.instant('dictionary.languages.ru'),
    },
    {
      id: 'UA',
      name: this._translate.instant('dictionary.languages.ua'),
    },
    {
      id: 'KZ',
      name: this._translate.instant('dictionary.languages.kz'),
    },
    {
      id: 'PL',
      name: this._translate.instant('dictionary.languages.pl'),
    },
    {
      id: 'AM',
      name: this._translate.instant('dictionary.languages.am'),
    },
    {
      id: 'ES',
      name: this._translate.instant('dictionary.languages.es'),
    },
  ];

  periods: DictionaryItem<number>[] = [
    { id: null, name: this._translate.instant('dictionary.periods.all') },
    { id: 1, name: this._translate.instant('dictionary.periods.month') },
    { id: 3, name: this._translate.instant('dictionary.periods.threeMonths') },
    { id: 6, name: this._translate.instant('dictionary.periods.sixMonths') },
    { id: 12, name: this._translate.instant('dictionary.periods.year') },
  ];

  events: DictionaryItem<string>[] = [
    { id: 'new_account', name: this._translate.instant('dictionary.events.newAccount') },
    { id: 'probation_end', name: this._translate.instant('dictionary.events.probationEnd') },
    { id: 'birthday', name: this._translate.instant('dictionary.events.birthday') },
    {
      id: 'personal_year_start',
      name: this._translate.instant('dictionary.events.personalYearStart'),
    },
  ];

  months: DictionaryItem<number>[] = [
    { id: 1, name: this._translate.instant('dictionary.months.january') },
    { id: 2, name: this._translate.instant('dictionary.months.february') },
    { id: 3, name: this._translate.instant('dictionary.months.march') },
    { id: 4, name: this._translate.instant('dictionary.months.april') },
    { id: 5, name: this._translate.instant('dictionary.months.may') },
    { id: 6, name: this._translate.instant('dictionary.months.june') },
    { id: 7, name: this._translate.instant('dictionary.months.july') },
    { id: 8, name: this._translate.instant('dictionary.months.august') },
    { id: 9, name: this._translate.instant('dictionary.months.september') },
    { id: 10, name: this._translate.instant('dictionary.months.october') },
    { id: 11, name: this._translate.instant('dictionary.months.november') },
    { id: 12, name: this._translate.instant('dictionary.months.december') },
  ];

  monthsKeys = [
    'dictionary.months.january',
    'dictionary.months.february',
    'dictionary.months.march',
    'dictionary.months.april',
    'dictionary.months.may',
    'dictionary.months.june',
    'dictionary.months.july',
    'dictionary.months.august',
    'dictionary.months.september',
    'dictionary.months.october',
    'dictionary.months.november',
    'dictionary.months.december',
  ];

  automaticAccrual: DictionaryItem<string>[] = [
    {
      id: 'monthly-group-tasks',
      name: this._translate.instant('admin.coins.automaticAccrual.table.type.monthlyGroup'),
      description: this._translate.instant(
        'admin.coins.automaticAccrual.table.description.automaticAccrueMonthly',
      ),
    },
    {
      id: 'yearly-group-tasks',
      name: this._translate.instant('admin.coins.automaticAccrual.table.type.yearlyGroup'),
      description: this._translate.instant(
        'admin.coins.automaticAccrual.table.description.automaticAccrueYearly',
      ),
    },
    {
      id: 'monthly-user-tasks',
      name: this._translate.instant('admin.coins.automaticAccrual.table.type.user'),
      description: this._translate.instant(
        'admin.coins.automaticAccrual.table.description.assingAnAward',
      ),
    },
  ];

  automaticTemplates: DictionaryItem<string>[] = [
    {
      id: 'birthday',
      name: this._translate.instant('admin.templates.automatic.table.type.birthday'),
    },
    {
      id: 'anniversary',
      name: this._translate.instant('admin.templates.automatic.table.type.anniversary'),
    },
    {
      id: 'newcomers',
      name: this._translate.instant('admin.templates.automatic.table.type.newcomers'),
    },
    {
      id: 'probation-end',
      name: this._translate.instant('admin.templates.automatic.table.type.probationEnd'),
    },
  ];

  manualTemplates: DictionaryItem<string>[] = [
    {
      id: 'wedding',
      name: this._translate.instant('admin.templates.manual.table.type.wedding'),
    },
    {
      id: 'birth-child',
      name: this._translate.instant('admin.templates.manual.table.type.birthChild'),
    },
    {
      id: 'promotion',
      name: this._translate.instant('admin.templates.manual.table.type.promotion'),
    },
  ];

  approvalStatuses: DictionaryItem<LimitApprovalStatus>[] = [
    { id: null, name: this._translate.instant('dictionary.approvalStatuses.all') },
    { id: 'auto', name: this._translate.instant('dictionary.approvalStatuses.auto') },
    { id: 'rejected', name: this._translate.instant('dictionary.approvalStatuses.rejected') },
    { id: 'inReview', name: this._translate.instant('dictionary.approvalStatuses.inReview') },
    { id: 'approved', name: this._translate.instant('dictionary.approvalStatuses.approved') },
  ];

  userRoles: DictionaryItem<UserRoles>[] = [
    { id: UserRoles.User, name: this._translate.instant('dictionary.userRoles.user') },
    { id: UserRoles.Admin, name: this._translate.instant('dictionary.userRoles.admin') },
  ];

  tgChatPermissions: DictionaryItem<TgChatPermissions>[] = [
    {
      id: TgChatPermissions.Member,
      name: this._translate.instant('dictionary.tgChatPermissions.member'),
    },
    {
      id: TgChatPermissions.Admin,
      name: this._translate.instant('dictionary.tgChatPermissions.admin'),
    },
  ];

  tgUserPermissions: DictionaryItem<TgUserPermissions>[] = [
    {
      id: TgUserPermissions.Member,
      name: this._translate.instant('dictionary.tgUserPermissions.member'),
    },
    {
      id: TgUserPermissions.Owner,
      name: this._translate.instant('dictionary.tgUserPermissions.owner'),
    },
    {
      id: TgUserPermissions.Admin,
      name: this._translate.instant('dictionary.tgUserPermissions.admin'),
    },
  ];

  tgChatTypes: DictionaryItem<TgChatTypes>[] = [
    {
      id: TgChatTypes.Channel,
      name: this._translate.instant('dictionary.tgChatTypes.channel'),
    },
    {
      id: TgChatTypes.Bot,
      name: this._translate.instant('dictionary.tgChatTypes.bot'),
    },
    {
      id: TgChatTypes.User,
      name: this._translate.instant('dictionary.tgChatTypes.user'),
    },
  ];

  userStatuses: DictionaryItem<UserStatusType>[] = [
    { id: 'active', name: this._translate.instant('dictionary.userStatuses.active') },
    { id: 'probation', name: this._translate.instant('dictionary.userStatuses.probation') },
    { id: 'offboarding', name: this._translate.instant('dictionary.userStatuses.offboarding') },
  ];

  sortTypes: SortType[] = [
    { id: 'id', name: this._translate.instant('dictionary.sortTypes.id') },
    { id: '-id', name: this._translate.instant('dictionary.sortTypes.idDesc') },
    { id: 'name', name: this._translate.instant('dictionary.sortTypes.name') },
    { id: '-name', name: this._translate.instant('dictionary.sortTypes.nameDesc') },
    { id: 'login', name: this._translate.instant('dictionary.sortTypes.login') },
    { id: '-login', name: this._translate.instant('dictionary.sortTypes.loginDesc') },
    { id: 'date', name: this._translate.instant('dictionary.sortTypes.date') },
    { id: '-date', name: this._translate.instant('dictionary.sortTypes.dateDesc') },
    { id: 'title', name: this._translate.instant('dictionary.sortTypes.title') },
    { id: '-title', name: this._translate.instant('dictionary.sortTypes.titleDesc') },
    { id: 'price', name: this._translate.instant('dictionary.sortTypes.price') },
    { id: '-price', name: this._translate.instant('dictionary.sortTypes.priceDesc') },
    { id: 'reward', name: this._translate.instant('dictionary.sortTypes.reward') },
    { id: '-reward', name: this._translate.instant('dictionary.sortTypes.rewardDesc') },
    { id: 'members', name: this._translate.instant('dictionary.sortTypes.members') },
    { id: '-members', name: this._translate.instant('dictionary.sortTypes.membersDesc') },
    { id: 'new', name: this._translate.instant('dictionary.sortTypes.new') },
    { id: 'sale', name: this._translate.instant('dictionary.sortTypes.sale') },
    { id: 'mostRecently', name: this._translate.instant('dictionary.sortTypes.mostRecently') },
    { id: 'brandName', name: this._translate.instant('dictionary.sortTypes.brand') },
    { id: '-brandName', name: this._translate.instant('dictionary.sortTypes.brandDesc') },
  ];

  modules: DictionaryItem<GroupTagEnum>[] = [
    { id: GroupTagEnum.Clubs, name: this._translate.instant('dictionary.groupTags.clubs') },
    { id: GroupTagEnum.Coins, name: this._translate.instant('dictionary.groupTags.normalCoins') },
    {
      id: GroupTagEnum.Communities,
      name: this._translate.instant('dictionary.groupTags.communities'),
    },
    { id: GroupTagEnum.Congrats, name: this._translate.instant('dictionary.groupTags.congrats') },
    {
      id: GroupTagEnum.CorpOnboarding,
      name: this._translate.instant('dictionary.groupTags.corpOnboarding'),
    },
    { id: GroupTagEnum.Courses, name: this._translate.instant('dictionary.groupTags.courses') },
    {
      id: GroupTagEnum.CoursesNotification,
      name: this._translate.instant('dictionary.groupTags.coursesNotification'),
    },
    {
      id: GroupTagEnum.EducationEvents,
      name: this._translate.instant('dictionary.groupTags.educationEvents'),
    },
    { id: GroupTagEnum.FAQ, name: this._translate.instant('dictionary.groupTags.faq') },
    { id: GroupTagEnum.Finances, name: this._translate.instant('dictionary.groupTags.finance') },
    { id: GroupTagEnum.Limits, name: this._translate.instant('dictionary.groupTags.limits') },
    {
      id: GroupTagEnum.MediaLibrary,
      name: this._translate.instant('dictionary.groupTags.mediaLibrary'),
    },
    { id: GroupTagEnum.Messages, name: this._translate.instant('dictionary.groupTags.messages') },
    { id: GroupTagEnum.News, name: this._translate.instant('dictionary.groupTags.news') },
    { id: GroupTagEnum.Survey, name: this._translate.instant('dictionary.groupTags.survey') },
  ];

  groupTags: DictionaryItem<GroupTagEnum>[] = [
    {
      id: GroupTagEnum.PinCoins,
      name: this._translate.instant('dictionary.groupTags.normalCoins'),
    },
    { id: GroupTagEnum.Courses, name: this._translate.instant('dictionary.groupTags.courses') },
    {
      id: GroupTagEnum.CoursesNotification,
      name: this._translate.instant('dictionary.groupTags.coursesNotification'),
    },
    { id: GroupTagEnum.FAQ, name: this._translate.instant('dictionary.groupTags.faq') },
    { id: GroupTagEnum.Messages, name: this._translate.instant('dictionary.groupTags.messages') },
    { id: GroupTagEnum.News, name: this._translate.instant('dictionary.groupTags.news') },
    { id: GroupTagEnum.Survey, name: this._translate.instant('dictionary.groupTags.survey') },
  ];

  transactionDirections: DictionaryItem<TransactionDirection>[] = [
    {
      id: TransactionDirection.Income,
      name: this._translate.instant('dictionary.transactionDirections.income'),
    },
    {
      id: TransactionDirection.Expense,
      name: this._translate.instant('dictionary.transactionDirections.expense'),
    },
  ];

  constructor(private _translate: TranslateService) {
  }

  filterSortTypes(...ids: string[]): SortType[] {
    const doubleIds = [...ids, ...ids.map(id => `-${id}`)];

    return this.sortTypes.filter(item => doubleIds.includes(item.id));
  }
}
